/**
 * Daily average pain series — used by chart/trend UIs and doctor‐visit
 * packets.
 *
 * Mirrors the daily-avg logic from both `buildPainByDateMap` (export) and
 * `buildTrendDataPoints` (Trends UI), exposing a single function that
 * supports both conventions via options.
 */

import type { PainRecord } from "./types";
import { mean, roundTo } from "./stats";

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/** One point in the daily pain series. */
export interface DailyPainPoint {
  /** ISO date string (YYYY-MM-DD) */
  date: string;
  /** Average pain intensity for the day, or `null` when no data / excluded. */
  avgPain: number | null;
  /** Number of pain location entries that day. */
  locationCount: number;
  /** Whether a record existed for this date (vs. a gap day). */
  hasData: boolean;
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * Compute a daily average pain series from records.
 *
 * **Export convention (default):** Records with 0 pain locations produce
 * `avgPain = null`. This is consistent with how `buildPainByDateMap`
 * works in the current export pipeline.
 *
 * **UI convention (`treatEmptyAsZero: true`):** Records with 0 pain
 * locations produce `avgPain = 0`. This matches `buildTrendDataPoints`
 * where a check-in with no locations is shown as zero-intensity.
 *
 * Results are sorted by date ascending.
 *
 * @param records  Daily pain records (need not be sorted).
 * @param opts.treatEmptyAsZero  If `true`, records with 0 locations produce 0 instead of null.
 */
export function computeDailyPainSeries(
  records: readonly PainRecord[],
  opts?: { treatEmptyAsZero?: boolean },
): DailyPainPoint[] {
  // Group by date — a date may appear multiple times if the caller provides
  // overlapping records (shouldn't happen, but defensive).
  const byDate = new Map<string, PainRecord[]>();
  for (const r of records) {
    const existing = byDate.get(r.recordDate);
    if (existing) {
      existing.push(r);
    } else {
      byDate.set(r.recordDate, [r]);
    }
  }

  const result: DailyPainPoint[] = [];
  for (const [date, dayRecords] of byDate) {
    // Flatten all pain locations for this date.
    const allLocations = dayRecords.flatMap((r) => r.painLocations);

    if (allLocations.length === 0) {
      result.push({
        date,
        avgPain: opts?.treatEmptyAsZero ? 0 : null,
        locationCount: 0,
        hasData: true,
      });
    } else {
      const intensities = allLocations.map((l) => l.intensityScore);
      result.push({
        date,
        avgPain: roundTo(mean(intensities), 1),
        locationCount: allLocations.length,
        hasData: true,
      });
    }
  }

  return result.sort((a, b) => a.date.localeCompare(b.date));
}
